import React, { useEffect, useMemo } from "react";

import { CheckBox } from "devextreme-react";
import Button from "devextreme-react/button";
import TreeList, {
  Column,
  HeaderFilter,
  Scrolling,
  SearchPanel,
  Selection,
  TreeListRef,
} from "devextreme-react/tree-list";
import { run, sleep } from "effection";
import { useTranslation } from "react-i18next";

import { useComponentStack, useWindowDimensions } from "@app/contexts";
import { EComponentStack, SIZES } from "@app/state/constants";
import { useFxDispatch, useFxSelector } from "@app/statefx";
import { schema } from "@app/statefx/schema";
import {
  fxInvoiceTrackTreeData,
  fxInvoiceTrackTreeDataFolded,
} from "@app/statefx/selectors/uiMonthTree";
import { provideNodeData } from "@app/statefx/xThunks/ds-invoice";
import { sfxPreserveTrackState } from "@app/statefx/xThunks/uiInvoiceTree";
import "@app/style/dx.generic.catetory-chooser-compact.css";
import "@app/style/dx.generic.catetory-chooser.css";

import "./invoiceTrack.scss";
import "./monthtrack.scss";

import type { TTrackTreeItem } from "@app/types/insider";
const CHECKBOX_HEIGHT = 40;

export const MonthExplorerV2 = ({ parent }: { parent: string }) => {
  const { components } = useComponentStack(EComponentStack.nop);
  const [rndr, setRndr] = React.useState(0);
  const [treeDataSource, setTreeDataSource] = React.useState(0);
  const once = React.useRef(false);
  const dispatch = useFxDispatch();
  const { t } = useTranslation();
  const { jgDrawerHeight } = useWindowDimensions();
  const treeRightRef = React.useRef<TreeListRef>(null);
  const currentState = useFxSelector(schema.uiInvoiceOverall.select);
  const theTreeData =
    treeDataSource === 0
      ? (useFxSelector(fxInvoiceTrackTreeDataFolded) as TTrackTreeItem[])
      : (useFxSelector(fxInvoiceTrackTreeData) as TTrackTreeItem[]);
  const nodeId = useMemo(() => [currentState?.currentItem], [currentState]);
  const treeToolbarHeight =
    parent === "spvMain" ? SIZES.treeSwitchHeightLarge : SIZES.treeSwitchHeight;

  const cssClss = useMemo(() => {
    return parent === "InvoiceMain"
      ? "dx-smaller-typography"
      : "dx-smaller-typography";
  }, [parent]);
  const checkBoxValue = useMemo(
    () => currentState?.includePrevious,
    [currentState]
  );

  function treeListOptionChange(e: any) {
    if (e.fullName === "selectedRowKeys") {
      const snodes = e.component.getSelectedRowKeys("excludeRecursive");
      dispatchStateChange(snodes[0], checkBoxValue);
    }
  }
  function checkboxOptionChange(e: any) {
    if (e.fullName === "value") {
      dispatchStateChange(nodeId[0], e.value);
    }
  }

  const draftCellRender = (e: any) => {
    return <div>{e?.data?.title}</div>;
  };

  const dispatchStateChange = (xNodeId: string, xCheckBoxValue: boolean) => {
    once.current = true; // prevent the onNodeInitialized from running

    dispatch(
      sfxPreserveTrackState({
        currentItem: xNodeId,
        includePrevious: xCheckBoxValue,
      })
    );

    const relevantComponent = components.includes(
      EComponentStack.InvoiceTracking
    )
      ? EComponentStack.InvoiceTracking
      : components.includes(EComponentStack.SpvMessages)
      ? EComponentStack.SpvMessages
      : EComponentStack.InvoicingInKarma;

    dispatch([
      provideNodeData({
        nodeId: xNodeId,
        includePrevious: Boolean(xCheckBoxValue),
        CIF: currentState.selectedCIF,
        stack: relevantComponent,
      }),
    ]);
  };

  function handleCollapsedOldItems(e: any) {
    const key = e.rows[e.newRowIndex]?.key;
    if (key === "older" || key === "collapse-older") {
      setTreeDataSource(key === "older" ? 1 : 0);
    }
  }

  function onRowPrepared(e: any) {
    if (e.rowType === "data" && ["older", "collapse-older"].includes(e.key)) {
      e.rowElement.classList.add(e.key);
    }
  }

  function* findAndExpandNode() {
    try {
      treeRightRef.current.instance().beginUpdate();
      yield* sleep(10); // we need to wait for the tree to be initialized
      const currentItemId = currentState.currentItem; // the recovered state from the disk cache
      if (currentItemId) {
        const itemAsNode = treeRightRef.current
          .instance()
          .getNodeByKey(currentItemId);
        if (!itemAsNode) {
          return;
        }
        if (itemAsNode.parent?.key) {
          treeRightRef.current.instance().expandRow(`${itemAsNode.parent.key}`);
        }
        treeRightRef.current.instance().expandRow(`${itemAsNode.key}`);
      }
    } finally {
      once.current = true;
      treeRightRef.current.instance().endUpdate();
    }
  }
  useEffect(() => {
    if (treeRightRef.current?.instance && !once.current) {
      requestIdleCallback(() => {
        run(() => findAndExpandNode());
      });
    }
  }, [theTreeData, currentState.currentItem]);
  useEffect(() => {
    // find nodeId
    if (theTreeData.length > 0) {
      const node = theTreeData.find((x) => x.id === nodeId[0]);
      if (!node) {
        dispatchStateChange(theTreeData[0].id, checkBoxValue);
      }
    }
  }, [treeDataSource]);

  useEffect(() => {
    if (treeRightRef.current?.instance) {
      const currentSelected = treeRightRef.current
        .instance()
        .getSelectedRowKeys();
      // if the first selected node is not the same as the current node
      if (currentSelected.length === 0 || currentSelected[0] !== nodeId[0]) {
        treeRightRef.current.instance().selectRows([nodeId], false);
      }
    }
  }, [nodeId]);

  return (
    <div className="invoiceTrackTree backlila">
      <Button
        className="tree-section-button"
        text={"Perioada"}
        stylingMode={"text"}
        type={"normal"}
        height={18}
        width={100}
      />
      <TreeList
        style={
          {
            "--treeSwitchHeight": `${SIZES.treeSwitchHeight}px`,
            "--checkboxHeight": `${CHECKBOX_HEIGHT}px`,
          } as React.CSSProperties
        }
        id={"invoice-treeview"}
        className={`dx-swatch-category-chooser-compact ${cssClss}`}
        ref={treeRightRef}
        height={jgDrawerHeight - CHECKBOX_HEIGHT - treeToolbarHeight}
        width={SIZES.headerButtonSize}
        dataSource={theTreeData}
        rootValue={null}
        keyExpr={"id"}
        parentIdExpr={"parent"}
        loadPanel={{ enabled: false }}
        selectedRowKeys={nodeId}
        noDataText={""}
        showColumnLines={false}
        showColumnHeaders={false}
        onRowPrepared={onRowPrepared}
        onFocusedCellChanging={handleCollapsedOldItems}
        onOptionChanged={treeListOptionChange}
      >
        <Selection recursive={true} mode="single" />
        <SearchPanel visible={false} />
        <Scrolling
          showScrollbar={"always"}
          preloadEnabled={true}
          mode={"standard"}
          useNative={true}
        />{" "}
        {/* "standard"| "virtual" | "infinite" */}
        <HeaderFilter visible={false} />
        <Column
          dataField="title"
          caption={""}
          allowSorting={false}
          allowFiltering={false}
          cellRender={draftCellRender}
        />
      </TreeList>
      <CheckBox
        id={"include_prior_values"}
        height={CHECKBOX_HEIGHT}
        width={SIZES.headerButtonSize}
        style={{ padding: "3px" }}
        className="dx-swatch-catetory-chooser-compact"
        text={t("include_prior_values")}
        onOptionChanged={checkboxOptionChange}
        // onValueChanged={dispatchStateChange}
      />
    </div>
  );
};
